@if (messages?.length) {
  <div
    #chatMessagesContainer
    class="chat-messages"
    infinite-scroll
    [scrollWindow]="false"
    [alwaysCallback]="true"
    [immediateCheck]="false"
    [infiniteScrollUpDistance]="3"
    [infiniteScrollThrottle]="300"
    [infiniteScrollDisabled]="hasLoadedAllPrevMessages || isLoadingPrevMessages"
    (scrolledUp)="onScrolledUp()"
  >
    @if (isLoadingPrevMessages) {
      <nimbus-spinner [transparent]="true"></nimbus-spinner>
    }
    @for (message of messages; track message.id; let i = $index) {
      @if (!message.hasBeenResent) {
        @if (message.isFirstOfTheDay) {
          <div class="day-separator">
            <span class="day-separator-label-text">{{ message.firstOfTheDayLabel ?? (message.dateTime | dateFormat: 'L' : false) }}</span>
          </div>
        }
        <div class="message-container" [ngClass]="message.authorType.toLowerCase()">
          @if (i === 0 || messages[i - 1].author !== message.author || message.authorType === AuthorType.System) {
            <div class="message-label">
              <i class="message-label-icon" [nimbusIcon]="getAuthorIcon(message.authorType)"></i>
              <span class="message-label-text">
                <span
                  [textContent]="hideAuthorTime ? message.author : message.author + ', ' + (message.dateTime | dateFormat: 'LT' : false)"
                ></span>
              </span>
            </div>
          }
          @if (message.content || message.media?.length) {
            <div class="message-box">
              <div class="arrow"></div>
              <div class="message-content">
                <div class="message-body">
                  @if (message.media?.length) {
                    <div class="message-media" [class.message-media-with-caption]="message.content && !message.isTypingContent">
                      @for (media of message.media; track $index) {
                        @let mediaKey = message.id + '_' + $index;
                        @let showMediaMetadata = $last && isMetadataInsideTile(message);
                        @if (media.blockReason !== MediaBlockReason.None) {
                          <ng-container
                            *ngTemplateOutlet="
                              fileMeta;
                              context: { media: media, message: message, showIcon: true, blocked: true, showMetadata: showMediaMetadata }
                            "
                          ></ng-container>
                        } @else if (hasMediaError(mediaKey)) {
                          <ng-container
                            *ngTemplateOutlet="
                              fileMeta;
                              context: {
                                media: media,
                                message: message,
                                showIcon: true,
                                failed: true,
                                mediaKey: mediaKey,
                                showMetadata: showMediaMetadata,
                              }
                            "
                          ></ng-container>
                        } @else if (media.kind === ChatMediaKind.Image) {
                          <img
                            class="message-image"
                            loading="lazy"
                            [src]="media.url"
                            [alt]="media.filename ?? ''"
                            (error)="onMediaError(mediaKey)"
                            (load)="onMediaLoaded()"
                            (click)="openMediaPreview(media)"
                          />
                        } @else if (media.kind === ChatMediaKind.Video) {
                          <div class="message-video" (click)="openMediaPreview(media)">
                            <video
                              muted
                              class="message-video-thumb"
                              preload="metadata"
                              [src]="media.url"
                              (error)="onMediaError(mediaKey)"
                              (loadedmetadata)="onMediaLoaded()"
                            ></video>
                            <i class="message-video-play" [nimbusIcon]="ICONS.PLAY"></i>
                          </div>
                        } @else if (media.kind === ChatMediaKind.Audio) {
                          <div class="message-audio-container">
                            <nimbus-input-media-player
                              class="message-audio"
                              [source]="media.url"
                              (isValidChanged)="onAudioValidChanged(mediaKey, $event)"
                            ></nimbus-input-media-player>
                            <ng-container
                              *ngTemplateOutlet="
                                fileMeta;
                                context: { media: media, message: message, showIcon: false, showMetadata: showMediaMetadata }
                              "
                            ></ng-container>
                          </div>
                        } @else {
                          <ng-container
                            *ngTemplateOutlet="
                              fileMeta;
                              context: { media: media, message: message, showIcon: true, showMetadata: showMediaMetadata }
                            "
                          ></ng-container>
                        }
                      }
                    </div>
                  }
                  @let isExpanded = !!expandedMessages[message.id];
                  @let formatted =
                    message.isTypingContent || message.isMarkdown
                      ? null
                      : (message.content | chatFormat: chatType | chatTruncate: expandMessageLength : isExpanded);
                  @let markdown =
                    message.isTypingContent || !message.isMarkdown ? null : (message.content | chatMarkdownTruncate: expandMessageLength);
                  @if (message.isTypingContent) {
                    <div class="typing-indicator">
                      @for (_ of [0, 1, 2]; track i; let i = $index) {
                        <div class="typing-dot"></div>
                      }
                    </div>
                  } @else if (markdown) {
                    <markdown
                      class="markdown-content"
                      [data]="markdown.isTruncated && !isExpanded ? markdown.content : message.content"
                    ></markdown>
                  } @else {
                    @if (message.contentIcon) {
                      <i class="message-content-icon" [nimbusIcon]="message.contentIcon"></i>
                    }
                    @for (block of formatted?.blocks; track $index) {
                      @switch (block.type) {
                        @case ('ordered-list') {
                          @for (line of block.lines; track $index; let itemIndex = $index) {
                            <div class="list-item">
                              <span class="list-marker">{{ block.orderedMarkers?.[itemIndex] ?? itemIndex + 1 }}.</span>
                              <span class="list-content">
                                @for (node of line; track $index) {
                                  <ng-container *ngTemplateOutlet="inlineNode; context: { $implicit: node }"></ng-container>
                                }
                              </span>
                            </div>
                          }
                        }
                        @case ('unordered-list') {
                          @for (line of block.lines; track $index) {
                            <div class="list-item">
                              <span class="list-marker">&bull;</span>
                              <span class="list-content">
                                @for (node of line; track $index) {
                                  <ng-container *ngTemplateOutlet="inlineNode; context: { $implicit: node }"></ng-container>
                                }
                              </span>
                            </div>
                          }
                        }
                        @case ('quote') {
                          <blockquote class="chat-format-quote">
                            @for (line of block.lines; track $index; let last = $last) {
                              @for (node of line; track $index) {
                                <ng-container *ngTemplateOutlet="inlineNode; context: { $implicit: node }"></ng-container>
                              }
                              @if (!last || isBlankFormatLine(line)) {
                                <br />
                              }
                            }
                          </blockquote>
                        }
                        @default {
                          @for (line of block.lines; track $index; let last = $last) {
                            @for (node of line; track $index) {
                              <ng-container *ngTemplateOutlet="inlineNode; context: { $implicit: node }"></ng-container>
                            }
                            @if (!last || isBlankFormatLine(line)) {
                              <br />
                            }
                          }
                        }
                      }
                    }
                  }

                  @if (formatted?.isTruncated || markdown?.isTruncated) {
                    <span class="read-more-less-text" (click)="toggleExpand(message.id, $event)">{{
                      (isExpanded ? TranslationKeys.ChatWidgetMessageReadLess : TranslationKeys.ChatWidgetMessageReadMore) | translate
                    }}</span>
                  }
                  @if (message.authorType !== AuthorType.System && !isMetadataInsideTile(message)) {
                    <ng-container *ngTemplateOutlet="messageMetadata; context: { message: message }"></ng-container>
                  }
                </div>
              </div>
            </div>
          }
          @if (messageExtensionTemplate) {
            <div class="message-extension">
              <ng-container *ngTemplateOutlet="messageExtensionTemplate; context: { message: message }"></ng-container>
            </div>
          }
          @if (isMessageSentFailed(message.status)) {
            <div class="message-failed">
              {{ TranslationKeys.ChatWidgetMessageFailedToSent | translate }}
              @if (!isDisabledState) {
                <span (click)="sendMessageAgain(message)">{{ TranslationKeys.ChatWidgetMessageFailedTryAgain | translate }}</span>
              }
            </div>
          }
          @if (isMessageUndelivered(message.status)) {
            <div class="message-not-delivered">
              @if (message.media?.length) {
                <div class="message-file-error">
                  <i class="message-file-error-icon" [nimbusIcon]="ICONS.EXCLAMATION_TRIANGLE_LIGTH"></i>
                  <ng-container *ngTemplateOutlet="undeliveredText; context: { message: message }"></ng-container>
                </div>
              } @else {
                <ng-container *ngTemplateOutlet="undeliveredText; context: { message: message }"></ng-container>
              }
            </div>
          }
          @if ((showActions && copyableAuthorTypes.includes(message.authorType) && !message.isTypingContent) || messageActionTemplate) {
            <div class="message-actions">
              @if (showActions && copyableAuthorTypes.includes(message.authorType) && !message.isTypingContent) {
                <span
                  class="message-action-icon"
                  [title]="TranslationKeys.GeneralCopy | translate"
                  [nimbusCopyClipboard]="message.content"
                  (copied)="onCopied()"
                >
                  <i [nimbusIcon]="ICONS.COPY"></i>
                </span>
                @if (authorInfoTemplate && message.showAuthorInfo) {
                  <nimbus-mat-icon-overlay class="message-actions-info" [icon]="ICONS.INFO_CIRCLE_LIGHT">
                    <ng-container *ngTemplateOutlet="authorInfoTemplate; context: { message }"></ng-container>
                  </nimbus-mat-icon-overlay>
                }
              }
              @if (messageActionTemplate && !message.isTypingContent) {
                <ng-container *ngTemplateOutlet="messageActionTemplate; context: { message: message }"></ng-container>
              }
            </div>
          }
        </div>
      }
    }
  </div>
} @else {
  @if (emptyTemplate) {
    <ng-container *ngTemplateOutlet="emptyTemplate"></ng-container>
  } @else {
    <div class="flex-container flex-align-center flex-column chat-messages-content-empty">
      <div>{{ TranslationKeys.ChatWidgetNotAvailable | translate }}</div>
    </div>
  }
}

<ng-template
  #fileMeta
  let-media="media"
  let-message="message"
  let-showIcon="showIcon"
  let-showMetadata="showMetadata"
  let-blocked="blocked"
  let-failed="failed"
  let-mediaKey="mediaKey"
>
  <div class="message-file">
    <div class="message-file-line">
      @if (showIcon) {
        <span class="message-file-icon-frame">
          <img class="message-file-icon" [src]="getMediaIcon(media)" alt="" />
        </span>
      }
      <span class="message-file-name text-truncate" [title]="media.filename">{{
        media.filename || (TranslationKeys.ChatWidgetMessageAttachment | translate)
      }}</span>
      @if (blocked) {
        <i class="message-file-blocked-icon tooltip-container" [nimbusIcon]="ICONS.LOCK_LIGHT">
          <nimbus-tooltip [text]="getBlockedTooltip(media)"></nimbus-tooltip>
        </i>
      } @else if (failed) {
        <button
          class="message-file-retry"
          type="button"
          [attr.aria-label]="TranslationKeys.GeneralRetry | translate"
          (click)="retryMedia(mediaKey, $event)"
        >
          <i [nimbusIcon]="ICONS.RELOAD"></i>
        </button>
      } @else if (!isMediaDownloadDisabled) {
        <button
          class="message-file-download"
          type="button"
          [attr.aria-label]="TranslationKeys.GeneralDownload | translate"
          (click)="downloadMedia(media)"
        >
          <i [nimbusIcon]="ICONS.DOWNLOAD" [iconWeight]="IconWeight.Light"></i>
        </button>
      }
    </div>
    <div class="message-file-line">
      <span class="message-file-size-inline">{{ getMediaSize(media) }}</span>
      @if (showMetadata) {
        <ng-container *ngTemplateOutlet="messageMetadata; context: { message: message }"></ng-container>
      }
    </div>
  </div>
  @if (failed) {
    <div class="message-file-error">
      <i class="message-file-error-icon" [nimbusIcon]="ICONS.EXCLAMATION_TRIANGLE_LIGTH"></i>
      <span>{{ TranslationKeys.ChatWidgetMessageAttachmentDownloadFailed | translate }}</span>
    </div>
  }
</ng-template>

<ng-template #messageMetadata let-message="message">
  <span class="message-metadata">
    <span class="message-time" [ngStyle]="{ 'margin-right': !message.status ? '3px' : '2px' }">
      {{ message.dateTime | dateFormat: 'LT' : false }}
    </span>
    @if (message.status && getStatusIcon(message.status)) {
      <span class="status-icon-wrapper">
        <i class="status-icon" [nimbusIcon]="getStatusIcon(message.status)" [ngClass]="{ 'icon-white': isMessageRead(message.status) }"></i>
        @if (showSecondCheckIcon(message.status)) {
          <i class="status-icon second-check-icon" [nimbusIcon]="ICONS.CHECK"></i>
        }
      </span>
    }
  </span>
</ng-template>

<ng-template #undeliveredText let-message="message">
  {{ TranslationKeys.ChatWidgetMessageFailedToSent | translate }}
  <i class="message-file-error-info tooltip-container" [nimbusIcon]="ICONS.INFO_CIRCLE_LIGHT">
    <nimbus-tooltip [text]="getAttachmentNotSentMessage(message)"></nimbus-tooltip>
  </i>
</ng-template>

<ng-template #inlineNode let-node>
  @switch (node.type) {
    @case ('bold') {
      <strong>
        @for (child of node.children; track $index) {
          <ng-container *ngTemplateOutlet="inlineNode; context: { $implicit: child }"></ng-container>
        }
      </strong>
    }
    @case ('italic') {
      <em>
        @for (child of node.children; track $index) {
          <ng-container *ngTemplateOutlet="inlineNode; context: { $implicit: child }"></ng-container>
        }
      </em>
    }
    @case ('strikethrough') {
      <s>
        @for (child of node.children; track $index) {
          <ng-container *ngTemplateOutlet="inlineNode; context: { $implicit: child }"></ng-container>
        }
      </s>
    }
    @case ('code') {
      <code class="chat-format-code" [textContent]="node.value"></code>
    }
    @case ('monospace') {
      <span class="chat-format-monospace" [textContent]="node.value"></span>
    }
    @case ('link') {
      <a [href]="node.href" target="_blank" rel="noopener noreferrer nofollow" [textContent]="node.value"></a>
    }
    @default {
      <span [textContent]="node.value"></span>
    }
  }
</ng-template>

@if (expandedMedia) {
  <div class="image-lightbox" (click)="closeMediaPreview()">
    <div class="image-lightbox-header" (click)="$event.stopPropagation()">
      <div class="image-lightbox-actions">
        @if (!isMediaDownloadDisabled) {
          <button
            class="image-lightbox-btn"
            type="button"
            [attr.aria-label]="TranslationKeys.GeneralDownload | translate"
            (click)="downloadMedia(expandedMedia)"
          >
            <i [nimbusIcon]="ICONS.DOWNLOAD"></i>
          </button>
        }
        <button
          class="image-lightbox-btn"
          type="button"
          [attr.aria-label]="TranslationKeys.GeneralClose | translate"
          (click)="closeMediaPreview()"
        >
          <i [nimbusIcon]="ICONS.CLOSE"></i>
        </button>
      </div>
    </div>
    <div class="image-lightbox-content">
      @if (expandedMedia.kind === ChatMediaKind.Video) {
        <video
          controls
          autoplay
          class="image-lightbox-img"
          controlsList="nodownload"
          [src]="expandedMedia.url"
          (click)="$event.stopPropagation()"
        ></video>
      } @else {
        <img class="image-lightbox-img" [src]="expandedMedia.url" [alt]="expandedMedia.filename ?? ''" (click)="$event.stopPropagation()" />
      }
    </div>
  </div>
}
